import { useState, useEffect, useCallback, useRef, type KeyboardEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { useHistory } from '@/hooks/useHistory'
import { showToast } from '@/components/layout/Toast'
import type { Cliente, ContactoVinculado, Empresa, CotizacionTarea, CotizacionTareaCostos, CotizacionItemsTareas, Seccion, EstadoCotizacion, Moneda, Cotizacion, TareaArtifact, ConfigTarifasCotizacion, PerfilEquipo, CostoLineItem } from '@/lib/types'
import { contactoFacturacionDe } from '@/lib/types'
import SelectorContactoEmpresa, { nombreContacto } from '@/components/contactos/SelectorContactoEmpresa'
import { formatCurrency, nextCotNumber, r2 } from '@/lib/utils'
import ArtifactUploader from '@/components/cotizaciones/ArtifactUploader'
import RichTextEditor from '@/components/common/RichTextEditor'
import PresupuestoTab from '@/components/cotizaciones/PresupuestoTab'
import { IconPlus, IconX, IconFile, IconEdit, IconGlobe, IconSettings, IconFileText, IconDollar, IconChevronDown, IconUndo, IconRedo, IconCheck } from '@/components/icons/Icons'
import { supabase } from '@/lib/supabase'
import { PageHeader, Breadcrumbs } from '@/components/ui'

/* ── Form snapshot for undo/redo + dirty tracking ── */
interface FormSnapshot {
  numero: string
  clienteId: number | null
  contacto: string
  telefono: string
  ruc: string
  email: string
  proyecto: string
  fecha: string
  validez: number
  moneda: Moneda
  estado: EstadoCotizacion
  igvPct: number
  descPct: number
  notas: string
  tareas: CotizacionTarea[]
  configTarifas: ConfigTarifasCotizacion
}

type SaveStatus = 'clean' | 'dirty' | 'saving' | 'saved'

const DEFAULT_CONFIG: ConfigTarifasCotizacion = {
  tarifa_dev_hora: 25,
  tarifa_qa_hora: 15,
  precio_input_mtok: 3,
  precio_thinking_mtok: 12,
  precio_output_mtok: 15,
  margen_global: 60,
}

const emptyTarea = (): CotizacionTarea => ({
  titulo: '',
  descripcion_corta: '',
  contenido_detalle: '',
  monto: 0,
  horas_estimadas: null,
  entregables: [],
})

/** Migrate legacy CotizacionTareaCostos to CostoLineItem[] */
function migrateCostosToItems(costos: CotizacionTareaCostos): CostoLineItem[] {
  const items: CostoLineItem[] = []
  if (costos.sesiones_ia > 0) {
    const tarifa = costos.tarifa_dev_hora || 25
    items.push({ id: crypto.randomUUID(), concepto: 'Sesiones IA (Dev)', cantidad: costos.sesiones_ia, unidad: 'sesiones', tarifa, subtotal: r2(costos.sesiones_ia * tarifa) })
  }
  if (costos.horas_qa > 0) {
    const tarifa = costos.tarifa_qa_hora || 15
    items.push({ id: crypto.randomUUID(), concepto: 'QA', cantidad: costos.horas_qa, unidad: 'horas', tarifa, subtotal: r2(costos.horas_qa * tarifa) })
  }
  if (costos.tokens_input_mtok > 0) {
    const tarifa = costos.precio_input_mtok || 3
    items.push({ id: crypto.randomUUID(), concepto: 'Tokens Input', cantidad: costos.tokens_input_mtok, unidad: 'MTok', tarifa, subtotal: r2(costos.tokens_input_mtok * tarifa) })
  }
  if (costos.tokens_thinking_mtok > 0) {
    const tarifa = costos.precio_thinking_mtok || 12
    items.push({ id: crypto.randomUUID(), concepto: 'Tokens Thinking', cantidad: costos.tokens_thinking_mtok, unidad: 'MTok', tarifa, subtotal: r2(costos.tokens_thinking_mtok * tarifa) })
  }
  if (costos.tokens_output_mtok > 0) {
    const tarifa = costos.precio_output_mtok || 15
    items.push({ id: crypto.randomUUID(), concepto: 'Tokens Output', cantidad: costos.tokens_output_mtok, unidad: 'MTok', tarifa, subtotal: r2(costos.tokens_output_mtok * tarifa) })
  }
  return items
}

type EditorTab = 'general' | 'tareas' | 'presupuesto'

export default function NuevaCotTareas() {
  const db = useSupabase()
  const nav = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  // Tab state
  const [activeTab, setActiveTab] = useState<EditorTab>('general')

  // Reference data
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [perfiles, setPerfiles] = useState<PerfilEquipo[]>([])
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('clean')

  // ── Undo/Redo ──
  const history = useHistory<FormSnapshot>(16)
  const isRestoringRef = useRef(false)
  const snapshotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedRef = useRef<string>('')
  const initialLoadRef = useRef(true)
  // Track the created record ID so auto-save after first manual create works
  const createdIdRef = useRef<number | null>(null)

  // Form state
  const [numero, setNumero] = useState('')
  const [clienteId, setClienteId] = useState<number | null>(null)
  const [contacto, setContacto] = useState('')
  const [clienteContactos, setClienteContactos] = useState<ContactoVinculado[]>([])
  const [contactoSelId, setContactoSelId] = useState<number | null>(null)
  const [telefono, setTelefono] = useState('')
  const [ruc, setRuc] = useState('')
  const [email, setEmail] = useState('')
  const [proyecto, setProyecto] = useState('')
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0])
  const [validez, setValidez] = useState(90)
  const [moneda, setMoneda] = useState<Moneda>('USD')
  const [estado, setEstado] = useState<EstadoCotizacion>('borrador')
  const [igvPct, setIgvPct] = useState(18)
  const [descPct, setDescPct] = useState(0)
  const [notas, setNotas] = useState('')
  const [tareas, setTareas] = useState<CotizacionTarea[]>([emptyTarea()])
  const [configTarifas, setConfigTarifas] = useState<ConfigTarifasCotizacion>(DEFAULT_CONFIG)

  // Entregable input state per task
  const [entregableInputs, setEntregableInputs] = useState<Record<number, string>>({})

  // Artifact uploader state
  const [artifactModalOpen, setArtifactModalOpen] = useState(false)
  const [artifactTareaIdx, setArtifactTareaIdx] = useState(0)

  // Expanded task in tareas tab
  const [expandedTarea, setExpandedTarea] = useState<number | null>(null)

  // Calculations
  const subtotal = r2(tareas.reduce((s, t) => s + t.monto, 0))
  const igvMonto = r2(subtotal * igvPct / 100)
  const descMonto = r2(subtotal * descPct / 100)
  const total = r2(subtotal + igvMonto - descMonto)

  // Load data
  useEffect(() => {
    (async () => {
      try {
        const [c, e, allCot, p] = await Promise.all([
          db.getAll<Cliente>('clientes'),
          db.getAll<Empresa>('empresa'),
          db.getAll<Cotizacion>('cotizaciones'),
          db.getAll<PerfilEquipo>('perfiles_equipo'),
        ])
        setClientes(c); setEmpresa(e[0] || null); setPerfiles(p)

        if (isEdit) {
          const cot = await db.getById<Cotizacion>('cotizaciones', parseInt(id!))
          if (cot) {
            setNumero(cot.numero); setClienteId(cot.cliente_id); setContacto(cot.cliente_contacto ?? '')
            setTelefono(cot.cliente_telefono ?? ''); setRuc(cot.cliente_ruc ?? ''); setEmail(cot.cliente_email ?? '')
            setProyecto(cot.proyecto_nombre ?? ''); setFecha(cot.fecha_emision ?? ''); setValidez(cot.validez_dias)
            setMoneda(cot.moneda as Moneda); setEstado(cot.estado); setIgvPct(cot.igv_porcentaje); setDescPct(cot.descuento_porcentaje)
            setNotas(cot.notas ?? '')
            // Extract tareas + config from items
            if (cot.items && typeof cot.items === 'object' && 'tareas' in cot.items) {
              const tareasData = cot.items as CotizacionItemsTareas
              // Migrate legacy costos → costos_items if needed
              const migratedTareas = tareasData.tareas.map(t => {
                if (t.costos && (!t.costos_items || t.costos_items.length === 0)) {
                  return { ...t, costos_items: migrateCostosToItems(t.costos) }
                }
                return t
              })
              // Recalculate montos from costos_items + margen_global to fix stale data
              const loadedConfig = tareasData.config_tarifas ?? DEFAULT_CONFIG
              const margen = loadedConfig.margen_global
              const divisor = 1 - margen / 100
              const recalculated = migratedTareas.map(t => {
                const items = t.costos_items ?? []
                if (items.length === 0) return t
                const costoTotal = r2(items.reduce((s, c) => s + c.subtotal, 0))
                const monto = divisor > 0 ? r2(costoTotal / divisor) : costoTotal
                return { ...t, monto }
              })
              setTareas(recalculated.length > 0 ? recalculated : [emptyTarea()])
              if (tareasData.config_tarifas) setConfigTarifas(tareasData.config_tarifas)
            }
          }
        } else {
          setNumero(nextCotNumber(allCot.map(q => q.numero)))
        }
      } catch { showToast('Error al cargar datos', 'error') }
      finally {
        setLoading(false)
        // After one render cycle with loaded data, capture initial snapshot
        setTimeout(() => {
          initialLoadRef.current = false
        }, 50)
      }
    })()
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize history + lastSaved after initial load completes
  useEffect(() => {
    if (loading || initialLoadRef.current) return
    const snap = getSnapshot()
    history.reset(snap)
    lastSavedRef.current = JSON.stringify(snap)
  }, [loading]) // eslint-disable-line react-hooks/exhaustive-deps

  function aplicarContacto(ct: ContactoVinculado | null) {
    setContactoSelId(ct?.id ?? null)
    setContacto(ct ? nombreContacto(ct) : '')
    setEmail(ct?.email ?? '')
    setTelefono(ct?.telefono ?? '')
  }

  // Fill client
  const fillCliente = useCallback((cId: number) => {
    setClienteId(cId)
    const c = clientes.find(x => x.id === cId)
    if (c) setRuc(c.ruc_dni ?? '')
    setClienteContactos(c?.contactos ?? [])
    aplicarContacto(contactoFacturacionDe(c))
  }, [clientes])

  // Tarea mutations
  const updateTarea = (idx: number, field: keyof CotizacionTarea, value: string | number | null | string[]) => {
    setTareas(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t))
  }
  const removeTarea = (idx: number) => setTareas(prev => prev.filter((_, i) => i !== idx))
  const addTarea = () => {
    setTareas(prev => [...prev, emptyTarea()])
    setExpandedTarea(tareas.length)
  }

  // Entregable management
  const addEntregable = (idx: number) => {
    const val = (entregableInputs[idx] ?? '').trim()
    if (!val) return
    setTareas(prev => prev.map((t, i) => i === idx ? { ...t, entregables: [...t.entregables, val] } : t))
    setEntregableInputs(prev => ({ ...prev, [idx]: '' }))
  }
  const removeEntregable = (tareaIdx: number, entIdx: number) => {
    setTareas(prev => prev.map((t, i) => i === tareaIdx ? { ...t, entregables: t.entregables.filter((_, j) => j !== entIdx) } : t))
  }
  const handleEntregableKey = (e: KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Enter') { e.preventDefault(); addEntregable(idx) }
  }

  // Artifact management
  const openArtifactUploader = (idx: number) => {
    setArtifactTareaIdx(idx)
    setArtifactModalOpen(true)
  }
  const addArtifact = (idx: number, artifact: TareaArtifact) => {
    setTareas(prev => prev.map((t, i) => i === idx
      ? { ...t, artifacts: [...(t.artifacts ?? []), artifact] }
      : t
    ))
  }
  const removeArtifact = async (tareaIdx: number, artifactId: string) => {
    const tarea = tareas[tareaIdx]
    const artifact = tarea.artifacts?.find(a => a.id === artifactId)
    if (artifact?.tipo === 'archivo' && artifact.storage_path) {
      await supabase.storage.from('cotizacion-artifacts').remove([artifact.storage_path])
    }
    setTareas(prev => prev.map((t, i) => i === tareaIdx
      ? { ...t, artifacts: (t.artifacts ?? []).filter(a => a.id !== artifactId) }
      : t
    ))
  }
  const artifactTypeIcon = (tipo: TareaArtifact['tipo']) => {
    switch (tipo) {
      case 'archivo': return <IconFile size={12} />
      case 'canvas': return <IconEdit size={12} />
      case 'google_doc': return <IconGlobe size={12} />
    }
  }
  const artifactTypeLabel = (tipo: TareaArtifact['tipo']) => {
    switch (tipo) {
      case 'archivo': return 'Archivo'
      case 'canvas': return 'Canvas'
      case 'google_doc': return 'Google Doc'
    }
  }

  // ── Snapshot helpers ──
  const getSnapshot = useCallback((): FormSnapshot => ({
    numero, clienteId, contacto, telefono, ruc, email, proyecto,
    fecha, validez, moneda, estado, igvPct, descPct, notas, tareas, configTarifas,
  }), [numero, clienteId, contacto, telefono, ruc, email, proyecto, fecha, validez, moneda, estado, igvPct, descPct, notas, tareas, configTarifas])

  const restoreSnapshot = useCallback((s: FormSnapshot) => {
    isRestoringRef.current = true
    setNumero(s.numero); setClienteId(s.clienteId); setContacto(s.contacto)
    setTelefono(s.telefono); setRuc(s.ruc); setEmail(s.email); setProyecto(s.proyecto)
    setFecha(s.fecha); setValidez(s.validez); setMoneda(s.moneda); setEstado(s.estado)
    setIgvPct(s.igvPct); setDescPct(s.descPct); setNotas(s.notas)
    setTareas(s.tareas); setConfigTarifas(s.configTarifas)
    // Allow next render cycle before re-enabling snapshot tracking
    setTimeout(() => { isRestoringRef.current = false }, 0)
  }, [])

  // ── Debounced snapshot push (500ms after last change) ──
  useEffect(() => {
    if (loading || initialLoadRef.current || isRestoringRef.current) return
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current)
    snapshotTimerRef.current = setTimeout(() => {
      const snap = getSnapshot()
      history.push(snap)
    }, 500)
    return () => { if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current) }
  }, [numero, clienteId, contacto, telefono, ruc, email, proyecto, fecha, validez, moneda, estado, igvPct, descPct, notas, tareas, configTarifas]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dirty tracking ──
  useEffect(() => {
    if (loading || initialLoadRef.current) return
    const currentJson = JSON.stringify(getSnapshot())
    if (currentJson !== lastSavedRef.current) {
      setSaveStatus('dirty')
    }
  }, [numero, clienteId, contacto, telefono, ruc, email, proyecto, fecha, validez, moneda, estado, igvPct, descPct, notas, tareas, configTarifas, getSnapshot, loading])

  // ── Persist to DB (shared between auto-save and manual save) ──
  const persistToDb = useCallback(async (silent = false): Promise<boolean> => {
    if (tareas.length === 0 || !tareas.some(t => t.titulo.trim())) {
      if (!silent) showToast('Agrega al menos una tarea con título', 'error')
      return false
    }
    setSaveStatus('saving')
    try {
      const clienteNombre = clientes.find(c => c.id === clienteId)?.empresa ?? ''
      // Recalculate montos from costos_items + margen to fix race condition
      // (when margen_global changes, setTareas may not have been processed yet)
      const margen = configTarifas.margen_global
      const divisor = 1 - margen / 100
      const recalcTareas = tareas.map(t => {
        const items = t.costos_items ?? []
        if (items.length === 0) return t
        const costoTotal = r2(items.reduce((s, c) => s + c.subtotal, 0))
        const monto = divisor > 0 ? r2(costoTotal / divisor) : costoTotal
        return { ...t, monto }
      })
      const recalcSubtotal = r2(recalcTareas.reduce((s, t) => s + t.monto, 0))
      const recalcIgv = r2(recalcSubtotal * igvPct / 100)
      const recalcDesc = r2(recalcSubtotal * descPct / 100)
      const recalcTotal = r2(recalcSubtotal + recalcIgv - recalcDesc)
      const itemsData: CotizacionItemsTareas = { tipo_cotizacion: 'tareas', tareas: recalcTareas, config_tarifas: configTarifas }
      const data: Record<string, unknown> = {
        numero, empresa_id: empresa?.id ?? 1, cliente_id: clienteId, cliente_nombre: clienteNombre,
        cliente_contacto: contacto, cliente_telefono: telefono, cliente_ruc: ruc, cliente_email: email,
        fecha_emision: fecha, validez_dias: validez, proyecto_nombre: proyecto,
        items: itemsData, subtotal: recalcSubtotal, igv_porcentaje: igvPct, igv_monto: recalcIgv,
        descuento_porcentaje: descPct, descuento_monto: recalcDesc, total: recalcTotal, moneda, estado,
        metodos_pago: ['Transferencia bancaria - clientes Perú', 'Tarjeta de débito/crédito - internacional', 'Criptomonedas - internacional'],
        secciones: [] as Seccion[], notas, tipo_cotizacion: 'tareas',
      }
      const editId = isEdit ? parseInt(id!) : createdIdRef.current
      if (editId) {
        data.id = editId
        await db.update('cotizaciones', data)
      } else {
        const created = await db.add('cotizaciones', data) as { id?: number } | undefined
        if (created?.id) createdIdRef.current = created.id
      }
      lastSavedRef.current = JSON.stringify(getSnapshot())
      setSaveStatus('saved')
      if (savedFlashTimerRef.current) clearTimeout(savedFlashTimerRef.current)
      savedFlashTimerRef.current = setTimeout(() => setSaveStatus('clean'), 2000)
      if (!silent) showToast(isEdit || createdIdRef.current ? 'Propuesta guardada' : 'Propuesta creada', 'success')
      return true
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al guardar', 'error')
      setSaveStatus('dirty')
      return false
    }
  }, [tareas, clientes, clienteId, configTarifas, numero, empresa, contacto, telefono, ruc, email, fecha, validez, proyecto, igvPct, descPct, moneda, estado, notas, isEdit, id, db, getSnapshot])

  // ── Manual save (for new cotizaciones or explicit save) ──
  const handleManualSave = useCallback(async () => {
    const ok = await persistToDb(false)
    if (ok && !isEdit && !createdIdRef.current) {
      nav('/cotizaciones')
    }
  }, [persistToDb, isEdit, nav])

  // ── Auto-save (only in edit mode or after first manual create, debounce 2s) ──
  useEffect(() => {
    if (loading || initialLoadRef.current) return
    const canAutoSave = isEdit || createdIdRef.current !== null
    if (!canAutoSave) return
    const currentJson = JSON.stringify(getSnapshot())
    if (currentJson === lastSavedRef.current) return

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(() => {
      persistToDb(true)
    }, 2000)
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }
  }, [numero, clienteId, contacto, telefono, ruc, email, proyecto, fecha, validez, moneda, estado, igvPct, descPct, notas, tareas, configTarifas]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Undo / Redo handlers ──
  const handleUndo = useCallback(() => {
    const prev = history.undo()
    if (prev) restoreSnapshot(prev)
  }, [history, restoreSnapshot])

  const handleRedo = useCallback(() => {
    const next = history.redo()
    if (next) restoreSnapshot(next)
  }, [history, restoreSnapshot])

  // ── Keyboard shortcuts (Ctrl+Z / Ctrl+Y) ──
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      // Don't intercept when focused in text inputs
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault()
        handleUndo()
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z') || (e.shiftKey && e.key === 'Z'))) {
        e.preventDefault()
        handleRedo()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleUndo, handleRedo])

  // ── Cleanup timers ──
  useEffect(() => {
    return () => {
      if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current)
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
      if (savedFlashTimerRef.current) clearTimeout(savedFlashTimerRef.current)
    }
  }, [])

  if (loading) return <div className="page-enter" style={{ padding: '2rem' }}><PageHeader breadcrumbs={[{ label: 'Cotizaciones', to: '/cotizaciones' }, { label: 'Propuesta por Tareas' }]} /><div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  const tabs: { key: EditorTab; label: string; icon: React.ReactNode }[] = [
    { key: 'general', label: 'General', icon: <IconSettings size={14} /> },
    { key: 'tareas', label: `Tareas (${tareas.filter(t => t.titulo.trim()).length})`, icon: <IconFileText size={14} /> },
    { key: 'presupuesto', label: 'Presupuesto', icon: <IconDollar size={14} /> },
  ]

  return (
    <div className="page-enter">
      <div className="page-header page-header--crumbs">
        <div>
          <Breadcrumbs
            items={[{ label: 'Cotizaciones', to: '/cotizaciones' }, { label: `${isEdit ? 'Editar' : 'Nueva'} — Propuesta por Tareas` }]}
            meta={`N° ${numero}`}
          />
        </div>
        <div className="cot-editor-actions">
          <button className="btn btn-secondary" onClick={() => nav(isEdit ? `/tareas-cot/${id}` : '/cotizaciones')}>Volver</button>
          <div className="cot-editor-history">
            <button
              type="button"
              className="cot-editor-history__btn"
              onClick={handleUndo}
              disabled={!history.canUndo}
              title="Deshacer (Ctrl+Z)"
            >
              <IconUndo size={15} />
            </button>
            <button
              type="button"
              className="cot-editor-history__btn"
              onClick={handleRedo}
              disabled={!history.canRedo}
              title="Rehacer (Ctrl+Y)"
            >
              <IconRedo size={15} />
            </button>
          </div>
          {(isEdit || createdIdRef.current) ? (
            <button
              className={`btn cot-editor-save cot-editor-save--${saveStatus}`}
              disabled={saveStatus === 'clean' || saveStatus === 'saving' || saveStatus === 'saved'}
              onClick={handleManualSave}
            >
              {saveStatus === 'saving' && <><span className="cot-editor-save__spinner" /> Guardando...</>}
              {saveStatus === 'saved' && <><IconCheck size={14} /> Guardado</>}
              {saveStatus === 'clean' && 'Sin cambios'}
              {saveStatus === 'dirty' && 'Guardar'}
            </button>
          ) : (
            <button className="btn btn-success" onClick={handleManualSave} disabled={saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Guardando...' : 'Guardar'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="cot-detail-tabs" style={{ marginBottom: 20 }}>
        {tabs.map(t => (
          <button
            key={t.key}
            className={`cot-detail-tab${activeTab === t.key ? ' cot-detail-tab--active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ══════ TAB: GENERAL ══════ */}
      {activeTab === 'general' && (
        <div className="editor-general-grid">
          {/* Client */}
          <div className="card">
            <div className="card-header"><h3>Datos del Cliente</h3></div>
            <div className="form-group">
              <label className="form-label">Cliente</label>
              <select className="form-select" value={clienteId ?? ''} onChange={e => fillCliente(parseInt(e.target.value))}>
                <option value="">— Seleccionar cliente —</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
              </select>
            </div>
            {clienteId && (
              <div className="form-group">
                <label className="form-label">Contacto de la empresa</label>
                <SelectorContactoEmpresa contactos={clienteContactos} value={contactoSelId} onSelect={aplicarContacto} />
              </div>
            )}
            <div className="form-row">
              <div className="form-group"><label className="form-label">Contacto</label><input className="form-input" value={contacto} onChange={e => setContacto(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">RUC/DNI</label><input className="form-input" value={ruc} onChange={e => setRuc(e.target.value)} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={telefono} onChange={e => setTelefono(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={email} onChange={e => setEmail(e.target.value)} /></div>
            </div>
          </div>

          {/* Project + Conditions */}
          <div>
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header"><h3>Proyecto</h3></div>
              <div className="form-group"><label className="form-label">Nombre</label><input className="form-input" value={proyecto} onChange={e => setProyecto(e.target.value)} placeholder="Ej: Desarrollo Bot WhatsApp" /></div>
            </div>
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header"><h3>Condiciones</h3></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Fecha</label><input className="form-input" type="date" value={fecha} onChange={e => setFecha(e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Validez (días)</label><input className="form-input" type="number" value={validez} onChange={e => setValidez(parseInt(e.target.value) || 90)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Moneda</label><select className="form-select" value={moneda} onChange={e => setMoneda(e.target.value as Moneda)}><option value="USD">USD</option><option value="PEN">PEN</option><option value="EUR">EUR</option></select></div>
                <div className="form-group"><label className="form-label">Estado</label><select className="form-select" value={estado} onChange={e => setEstado(e.target.value as EstadoCotizacion)}><option value="borrador">Borrador</option><option value="enviada">Enviada</option><option value="aceptada">Aceptada</option><option value="rechazada">Rechazada</option></select></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">IGV %</label><input className="form-input" type="number" value={igvPct} onChange={e => setIgvPct(parseFloat(e.target.value) || 0)} /></div>
                <div className="form-group"><label className="form-label">Descuento %</label><input className="form-input" type="number" value={descPct} onChange={e => setDescPct(parseFloat(e.target.value) || 0)} /></div>
              </div>
            </div>
            <div className="card">
              <div className="card-header"><h3>Notas</h3></div>
              <textarea className="form-textarea" value={notas} onChange={e => setNotas(e.target.value)} placeholder="Notas adicionales..." />
            </div>
          </div>
        </div>
      )}

      {/* ══════ TAB: TAREAS ══════ */}
      {activeTab === 'tareas' && (
        <div>
          <div className="card">
            <div className="card-header">
              <h3>Tareas de la Propuesta</h3>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{tareas.length} tarea{tareas.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Task table */}
            <table className="presupuesto-table" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th>Tarea</th>
                  <th style={{ width: 100 }}>Monto</th>
                  <th style={{ width: 80 }}>Horas</th>
                  <th style={{ width: 80 }}>Entregables</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {tareas.map((t, idx) => (
                  <>
                    <tr
                      key={`row-${idx}`}
                      className={expandedTarea === idx ? 'presupuesto-table__row--active' : ''}
                      onClick={() => setExpandedTarea(expandedTarea === idx ? null : idx)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ color: 'var(--primary-light)', fontWeight: 700 }}>{idx + 1}</td>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{t.titulo || 'Sin título'}</div>
                        {t.descripcion_corta && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{t.descripcion_corta}</div>}
                      </td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(t.monto, moneda)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{t.horas_estimadas ?? '—'}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{t.entregables.length}</td>
                      <td>
                        <span style={{ transition: 'transform 0.2s', display: 'inline-block', transform: expandedTarea === idx ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                          <IconChevronDown size={14} />
                        </span>
                      </td>
                    </tr>
                    {expandedTarea === idx && (
                      <tr key={`detail-${idx}`}>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <div className="editor-task-expanded">
                            <div className="form-group">
                              <label className="form-label">Título</label>
                              <input className="form-input" value={t.titulo} onChange={e => updateTarea(idx, 'titulo', e.target.value)} placeholder="Ej: Setup infraestructura" />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Descripción corta (resumen)</label>
                              <input className="form-input" value={t.descripcion_corta} onChange={e => updateTarea(idx, 'descripcion_corta', e.target.value)} placeholder="1-2 líneas para la tabla resumen" />
                            </div>

                            {/* Entregables */}
                            <div className="form-group">
                              <label className="form-label">Entregables</label>
                              <div className="task-editor-tags">
                                {t.entregables.map((ent, j) => (
                                  <span key={j} className="tag">{ent} <button onClick={() => removeEntregable(idx, j)}>✕</button></span>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                <input
                                  className="form-input"
                                  value={entregableInputs[idx] ?? ''}
                                  onChange={e => setEntregableInputs(prev => ({ ...prev, [idx]: e.target.value }))}
                                  onKeyDown={e => handleEntregableKey(e, idx)}
                                  placeholder="Agregar entregable + Enter"
                                  style={{ flex: 1 }}
                                />
                                <button className="btn btn-sm btn-secondary" onClick={() => addEntregable(idx)}>+</button>
                              </div>
                            </div>

                            {/* Detail content */}
                            <div className="form-group">
                              <label className="form-label">Detalle de la tarea</label>
                              <RichTextEditor
                                content={t.contenido_detalle}
                                onChange={html => updateTarea(idx, 'contenido_detalle', html)}
                                placeholder="Escribe la descripción detallada de la tarea..."
                                minHeight={200}
                              />
                            </div>

                            {/* Artifacts */}
                            <div className="form-group">
                              <label className="form-label">Documentos Adjuntos</label>
                              {(t.artifacts ?? []).length > 0 && (
                                <div className="artifact-edit-list">
                                  {(t.artifacts ?? []).map(a => (
                                    <div key={a.id} className="artifact-edit-item">
                                      {artifactTypeIcon(a.tipo)}
                                      <span className="artifact-edit-item__name">{a.nombre}</span>
                                      <span className="artifact-edit-item__type">{artifactTypeLabel(a.tipo)}</span>
                                      <button className="artifact-edit-item__remove" onClick={() => removeArtifact(idx, a.id)} title="Eliminar"><IconX size={12} /></button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <button className="btn btn-sm btn-secondary" onClick={() => openArtifactUploader(idx)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <IconPlus size={12} /> Agregar Artifact
                              </button>
                            </div>

                            {/* Remove task */}
                            {tareas.length > 1 && (
                              <button className="btn btn-sm btn-danger" onClick={() => { removeTarea(idx); setExpandedTarea(null) }} style={{ marginTop: 8 }}>
                                <IconX size={12} /> Eliminar tarea
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>

            <button className="btn btn-primary" style={{ width: '100%' }} onClick={addTarea}><IconPlus size={14} /> Agregar Tarea</button>
          </div>

          {/* Financial summary */}
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-header"><h3>Resumen Financiero</h3></div>
            <div style={{ fontSize: '0.9rem' }}>
              {tareas.filter(t => t.titulo.trim()).map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{i + 1}. {t.titulo || 'Sin título'}</span>
                  <strong>{formatCurrency(t.monto, moneda)}</strong>
                </div>
              ))}
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '2px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}><span>Subtotal</span><strong>{formatCurrency(subtotal, moneda)}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}><span>IGV ({igvPct}%)</span><strong>{formatCurrency(igvMonto, moneda)}</strong></div>
                {descPct > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}><span>Descuento ({descPct}%)</span><strong>-{formatCurrency(descMonto, moneda)}</strong></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '1.1rem', color: 'var(--success)', borderTop: '1px solid var(--border)', marginTop: 4 }}><span style={{ fontWeight: 700 }}>TOTAL</span><strong>{formatCurrency(total, moneda)}</strong></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════ TAB: PRESUPUESTO ══════ */}
      {activeTab === 'presupuesto' && (
        <PresupuestoTab
          tareas={tareas}
          setTareas={setTareas}
          config={configTarifas}
          setConfig={setConfigTarifas}
          perfiles={perfiles}
          moneda={moneda}
          formatCurrency={formatCurrency}
          subtotal={subtotal}
          igvMonto={igvMonto}
          descMonto={descMonto}
          total={total}
          igvPct={igvPct}
          descPct={descPct}
        />
      )}

      {/* Artifact Uploader Modal */}
      <ArtifactUploader
        isOpen={artifactModalOpen}
        onClose={() => setArtifactModalOpen(false)}
        onAdd={artifact => addArtifact(artifactTareaIdx, artifact)}
        cotizacionId={isEdit ? parseInt(id!) : null}
        tareaIdx={artifactTareaIdx}
      />
    </div>
  )
}
